import { useEffect, useState } from "react";
import Camera from "react-html5-camera-photo";
import { useTranslation } from "react-i18next";
import CameraSwitchIcon from "@mui/icons-material/Cameraswitch";
import styled from "styled-components";
import { CSS_VARIABLES } from "enums/cssVariables";
import { isMobile } from "react-device-detect";
import "react-html5-camera-photo/build/css/index.css";

type Props = {
    onTakePhoto: (dataUri: string) => void;
    onClose: () => void;
};

export const PhotoCamera = ({ onTakePhoto, onClose }: Props) => {
    const { i18n } = useTranslation();
    const [facingMode, setFacingMode] = useState<"user" | "environment">(
        "environment",
    );
    const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
    const [cameraPermission, setCameraPermission] = useState<
        "granted" | "denied" | "prompt"
    >("prompt");

    useEffect(() => {
        if (!navigator.permissions) return;

        let permissionStatus: PermissionStatus;

        async function monitorPermission() {
            try {
                permissionStatus = await navigator.permissions.query({
                    name: "camera",
                });
                setCameraPermission(permissionStatus.state);

                permissionStatus.onchange = () => {
                    setCameraPermission(permissionStatus.state);
                };
            } catch (err) {
                console.warn("Permissions API not fully supported", err);
            }
        }

        monitorPermission();

        return () => {
            if (permissionStatus) permissionStatus.onchange = null;
        };
    }, []);

    useEffect(() => {
        async function checkCameras() {
            if (cameraPermission !== "granted") return;
            if (
                !navigator.mediaDevices ||
                !navigator.mediaDevices.enumerateDevices
            )
                return;
            try {
                const devices = await navigator.mediaDevices.enumerateDevices();
                const videoDevices = devices.filter(
                    (d) => d.kind === "videoinput",
                );
                setHasMultipleCameras(videoDevices.length > 1);
            } catch (err) {
                console.warn("Cannot enumerate devices", err);
            }
        }

        checkCameras();
    }, [cameraPermission]);

    return (
        <CameraOverlay>
            <CameraContainer>
                <Camera
                    isImageMirror={false}
                    onTakePhoto={onTakePhoto}
                    idealFacingMode={facingMode}
                />
                <CameraActions>
                    <StyledButton
                        onClick={onClose}
                        aria-label={i18n.t("photo.close")}
                    >
                        {i18n.t("photo.close")}
                    </StyledButton>
                    {(hasMultipleCameras || isMobile) && (
                        <StyledOutlineButton
                            onClick={() =>
                                setFacingMode(
                                    facingMode === "user"
                                        ? "environment"
                                        : "user",
                                )
                            }
                        >
                            <CameraSwitchIcon style={{ marginRight: 8 }} />
                            {i18n.t("photo.switchCamera")}
                        </StyledOutlineButton>
                    )}
                </CameraActions>
            </CameraContainer>
        </CameraOverlay>
    );
};

export default PhotoCamera;

const StyledOutlineButton = styled.button.attrs({ type: "button" })`
    display: flex;
    align-items: center;
    padding: 10px 18px;
    margin: 0.5rem 0.5rem;
    cursor: pointer;
    background-color: #fafafa;
    border-radius: 12px;
    border: 1px solid var(${CSS_VARIABLES.PRIMARY_BUTTON_BACKGROUND_COLOR});
    color: var(${CSS_VARIABLES.PRIMARY_BUTTON_BACKGROUND_COLOR});
    :hover {
        cursor: pointer;
        filter: brightness(0.8);
    }
    :focus-visible {
        outline: solid 2px var(${CSS_VARIABLES.PRIMARY_BUTTON_BACKGROUND_COLOR});
    }
    :disabled {
        cursor: not-allowed;
        filter: brightness(0.8);
    }
`;

const StyledButton = styled(StyledOutlineButton)`
    background-color: var(${CSS_VARIABLES.PRIMARY_BUTTON_BACKGROUND_COLOR});
    border: 1px solid var(${CSS_VARIABLES.PRIMARY_BUTTON_BACKGROUND_COLOR});
    border-radius: 12px;
    color: #fff;
    margin-right: 0;
    :focus-visible {
        outline: solid 2px var(${CSS_VARIABLES.PRIMARY_BUTTON_BACKGROUND_COLOR});
    }
`;

const CameraOverlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;

const CameraContainer = styled.div`
    background: #fff;
    padding: 10px;
    border-radius: 12px;
    max-width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const CameraActions = styled.div`
    margin-top: 16px;
    display: flex;
    gap: 12px;
`;
